import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AdminSurveyService } from './admin-survey.service';

export interface SurveyPrintProgress {
  progress: number;
  currentPage: number;
  totalPages: number;
  message: string;
}

export interface PrintItem {
  type: 'section-header' | 'question';
  sectionTitle: string;
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export class SurveyPrintService {
  private pdfBlob: Blob | null = null;
  private currentAbortController: AbortController | null = null;
  public progress$ = new Subject<SurveyPrintProgress>();
  private currentSurveyData: any = null;

  constructor(private adminSurveyService: AdminSurveyService) {}

  async generateSurveyPDF(surveyData: any): Promise<void> {
    if (!surveyData) {
      throw new Error('Survey data not found');
    }

    this.currentSurveyData = surveyData;

    this.currentAbortController = new AbortController();
    const signal = this.currentAbortController.signal;

    try {
      const updatedSurveyData = await this.loadSignedLinksForAssets(surveyData);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const printItems = this.flattenSectionsForPrint(updatedSurveyData);

      const totalPages = this.calculateTotalPages(printItems) + 1;

      this.progress$.next({
        progress: 10,
        currentPage: 0,
        totalPages: totalPages,
        message: 'Preparing survey content...',
      });

      await this.addCoverPage(
        pdf,
        updatedSurveyData,
        updatedSurveyData.assets || {},
        signal
      );

      this.progress$.next({
        progress: 30,
        currentPage: 1,
        totalPages: totalPages,
        message: 'Adding survey header...',
      });

      if (printItems && printItems.length > 0) {
        //  Log space calculations for debugging (uncomment if needed)
        // this.logQuestionSpaceCalculations(printItems);

        await this.addQuestionPages(
          pdf,
          printItems,
          updatedSurveyData.assets,
          signal
        );
      }

      const pdfOutput = pdf.output('arraybuffer');
      this.pdfBlob = new Blob([pdfOutput], { type: 'application/pdf' });

      this.progress$.next({
        progress: 100,
        currentPage: totalPages,
        totalPages: totalPages,
        message: 'Survey PDF ready!',
      });
    } catch (error) {
      if (signal.aborted) {
        throw new Error('PDF generation was cancelled');
      }
      throw error;
    } finally {
      this.currentAbortController = null;
    }
  }

  private flattenSectionsForPrint(surveyData: any): PrintItem[] {
    const printItems: PrintItem[] = [];

    const questionsMap = new Map<string, any>();
    if (surveyData.questions && surveyData.questions.length > 0) {
      surveyData.questions.forEach((question: any) => {
        questionsMap.set(question.questionId, question);
      });
    }

    if (
      surveyData.useSections &&
      surveyData.sections &&
      surveyData.sections.length > 0
    ) {
      surveyData.sections.forEach((section: any) => {
        // Only add section header if hideHeader is not true
        if (!section.hideHeader) {
          printItems.push({
            type: 'section-header',
            sectionTitle: section.title || 'Untitled Section',
            data: section,
          });
        }

        // Resolve questions from questionIds (source of truth)
        const sectionQuestions: any[] = [];
        if (section.questionIds && section.questionIds.length > 0) {
          section.questionIds.forEach((questionId: string) => {
            const question = questionsMap.get(questionId);
            if (question) {
              sectionQuestions.push(question);
            }
          });
        } else if (section.questions && section.questions.length > 0) {
          // Fallback to legacy questions array
          sectionQuestions.push(...section.questions);
        }

        sectionQuestions.forEach((question: any) => {
          printItems.push({
            type: 'question',
            sectionTitle: section.title || 'Untitled Section',
            data: question,
          });
        });
      });
    } else {
      if (surveyData.questions && surveyData.questions.length > 0) {
        surveyData.questions.forEach((question: any) => {
          printItems.push({
            type: 'question',
            sectionTitle: '',
            data: question,
          });
        });
      }
    }

    return printItems;
  }

  private async loadSignedLinksForAssets(surveyData: any): Promise<any> {
    try {
      if (surveyData.uuid === '-1') {
        // console.log('New survey detected, using existing asset buffers');
        return surveyData;
      }

      const imageKeys = Object.keys(surveyData.assets || {}).map((key) =>
        key.replace(/-thumb$/, '')
      );

      if (surveyData.image && typeof surveyData.image === 'string') {
        imageKeys.push(surveyData.image);
      }

      if (surveyData.questions) {
        surveyData.questions.forEach((question: any) => {
          if (question.metadata?.assets) {
            Object.values(question.metadata.assets).forEach((asset: any) => {
              if (asset?.imageKey) {
                const key = asset.imageKey.replace(/-thumb$/, '');
                if (!imageKeys.includes(key)) {
                  imageKeys.push(key);
                }
              }
            });
          }
        });
      }

      if (imageKeys.length === 0) {
        return surveyData;
      }

      const assetResponse =
        await this.adminSurveyService.getSurveyAssets(imageKeys);

      const updatedSurveyData = {
        ...surveyData,
        assets: {
          ...(surveyData.assets || {}),
          ...(assetResponse.response || {}),
        },
      };

      return updatedSurveyData;
    } catch (error) {
      return surveyData;
    }
  }

  private async addQuestionPages(
    pdf: jsPDF,
    questions: any[],
    assets: any,
    signal: AbortSignal
  ): Promise<void> {
    let currentPage = 1;
    let itemIndex = 0;
    let questionNumberCursor = 1;

    while (itemIndex < questions.length) {
      if (signal.aborted) throw new Error('PDF generation was cancelled');

      const pageQuestions = this.getQuestionsForPage(questions, itemIndex);
      if (pageQuestions.length === 0) {
        break;
      }

      const pageQuestionCount = pageQuestions.filter(
        (item) => item.type === 'question'
      ).length;
      const startQuestion = questionNumberCursor;
      const endQuestion = questionNumberCursor + pageQuestionCount - 1;
      const progressMessage =
        pageQuestionCount > 0
          ? `Processing questions ${startQuestion}-${endQuestion}...`
          : 'Processing section content...';

      this.progress$.next({
        progress: 30 + (itemIndex / questions.length) * 60,
        currentPage: currentPage + 1,
        totalPages: this.calculateTotalPages(questions) + 1,
        message: progressMessage,
      });

      pdf.addPage();

      await this.addQuestionPage(
        pdf,
        pageQuestions,
        startQuestion,
        assets,
        signal
      );

      itemIndex += pageQuestions.length;
      questionNumberCursor += pageQuestionCount;
      currentPage++;
    }
  }

  private async addQuestionPage(
    pdf: jsPDF,
    questions: any[],
    startNumber: number,
    assets: any,
    signal: AbortSignal
  ): Promise<void> {
    if (signal.aborted) throw new Error('PDF generation was cancelled');

    const pageContainer = this.createPageContainer();

    const surveyTitle =
      this.currentSurveyData?.title || this.currentSurveyData?.name || 'Survey';
    const headerBar = `
      <div style="width: 100%; padding: 12px 20px; background: linear-gradient(135deg, #007cba 0%, #005a8b 100%); color: white; margin-bottom: 30px; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
        <div style="font-size: 16px; font-weight: 600; text-align: center;">
          ${this.escapeHtml(surveyTitle)}
        </div>
        <div style="font-size: 12px; text-align: center; margin-top: 4px; opacity: 0.9;">
          Questions ${startNumber} - ${startNumber + questions.length - 1}
        </div>
      </div>
    `;

    let questionsHtml = `<div style="flex: 1;">${headerBar}`;

    let questionCounter = 0;
    questions.forEach((item) => {
      if (item.type === 'section-header') {
        questionsHtml += this.generateSectionHeaderHtml(
          item.sectionTitle,
          item.data.instructions
        );
      } else {
        const question = item.type === 'question' ? item.data : item;
        const questionNumber = startNumber + questionCounter;
        questionsHtml += this.generateQuestionHtml(
          question,
          questionNumber,
          assets
        );
        questionCounter++;
      }
    });

    questionsHtml += '</div>';

    questionsHtml += `
      <div style="margin-top: auto; text-align: center; padding: 12px 0; border-top: 1px solid #e0e0e0; background: white; font-size: 0.7rem; color: #999;">
        Generated by Warfighter Touchpoint App
      </div>
    `;

    pageContainer.innerHTML = questionsHtml;
    await this.renderPageToPdf(pdf, pageContainer);
  }

  private async addCoverPage(
    pdf: jsPDF,
    surveyData: any,
    assets: any,
    signal: AbortSignal
  ): Promise<void> {
    if (signal.aborted) throw new Error('PDF generation was cancelled');

    const pageContainer = this.createPageContainer();

    const surveyImageKey = surveyData?.image;
    let surveyImageSrc = null;

    if (surveyImageKey) {
      const imageFromAssets = assets[surveyImageKey];
      const imageFromSurveyAssets = surveyData?.assets?.[surveyImageKey];

      if (imageFromSurveyAssets) {
        surveyImageSrc = this.processImageSrc(imageFromSurveyAssets);
      } else if (imageFromAssets) {
        surveyImageSrc = this.processImageSrc(imageFromAssets);
      }
    }

    const coverPageHtml = `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: flex-start; text-align: center; padding: 2rem 1rem; box-sizing: border-box; flex: 1;">

        ${
          surveyData?.eventName
            ? `
          <div style="font-size: 12px; color: #666; margin-bottom: 1.5rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 500;">
            ${this.escapeHtml(surveyData.eventName)}
          </div>
        `
            : ''
        }


        <h1 style="font-size: 2rem; font-weight: bold; margin: 0 0 1.5rem 0; color: #000; line-height: 1.2; max-width: 90%;">
          ${this.escapeHtml(surveyData?.title || surveyData?.name || 'Survey')}
        </h1>


        ${
          surveyData?.description
            ? `
          <div style="font-size: 1rem; color: #555; line-height: 1.5; max-width: 85%; margin-bottom: 2rem; text-align: center;">
            ${this.processRichText(surveyData.description)}
          </div>
        `
            : ''
        }

        <!-- Survey Instructions -->
        ${
          surveyData?.instructions
            ? `
          <div style="font-size: 1rem; color: #555; line-height: 1.5; max-width: 85%; margin-bottom: 2rem; text-align: left; margin-left: 0; margin-right: auto; padding-left: 50px;">
            ${this.processRichText(surveyData.instructions)}
          </div>
        `
            : ''
        }

        ${
          surveyImageSrc
            ? `
          <div style="margin-bottom: 2.5rem; text-align: center;">
            <img src="${surveyImageSrc}"
                 alt="Survey Image"
                 crossorigin="anonymous"
                 style="max-width: 350px; max-height: 250px; width: auto; height: auto; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); display: block; margin: 0 auto; border: 1px solid #e0e0e0;" />
          </div>
        `
            : ''
        }


        <div style="margin-top: 3rem; padding: 2rem; background: #fafafa; border-radius: 4px; border-left: 4px solid #007cba; max-width: 85%; width: 100%; box-sizing: border-box;">
          <p style="margin: 0 0 1.5rem 0; font-size: 1rem; color: #333; line-height: 1.6;">
            Please read each question carefully and provide your responses. Questions marked as <strong style="color: #dc3545;">"Required"</strong> must be answered to proceed.
          </p>
          <div style="padding-top: 1rem; border-top: 1px solid #e0e0e0; font-size: 0.9rem; color: #666; display: flex; justify-content: space-between;">
            <span>Total Questions: <strong>${surveyData?.questions?.length || 0}</strong></span>
            <span>Generated: <strong>${new Date().toLocaleDateString()}</strong></span>
          </div>
        </div>


        <div style="margin-top: auto; text-align: center; padding: 12px 0; border-top: 1px solid #e0e0e0; background: white;">
          <p style="margin: 0; font-size: 0.7rem; color: #666;">
            Generated by Warfighter Touchpoint App on ${new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    `;

    pageContainer.innerHTML = coverPageHtml;
    await this.renderPageToPdf(pdf, pageContainer);
  }

  private calculateTotalPages(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;

    let totalPages = 0;
    let questionIndex = 0;

    while (questionIndex < questions.length) {
      const pageQuestions = this.getQuestionsForPage(questions, questionIndex);
      if (pageQuestions.length === 0) {
        // Safety check to prevent infinite loop
        break;
      }
      totalPages++;
      questionIndex += pageQuestions.length;
    }

    return totalPages;
  }

  private getQuestionsForPage(items: any[], startIndex: number): any[] {
    if (startIndex >= items.length) return [];

    const pageItems = [];
    let currentSpace = 0;
    let lockedFirstQuestionIndex: number | null = null;

    // A4 page is ~297mm, minus margins, header (~40mm), footer (~20mm) = ~220mm usable
    const maxSpacePerPage = 240; // Reduced to prevent cut-offs and ensure footer fits

    for (let i = startIndex; i < items.length; i++) {
      const item = items[i];
      const itemSpace = this.calculateQuestionSpace(item);
      const isForcedPairItem =
        lockedFirstQuestionIndex !== null && i <= lockedFirstQuestionIndex;

      if (item.type === 'section-header') {
        const nextQuestionIndex = items.findIndex(
          (nextItem, idx) => idx > i && nextItem.type === 'question'
        );

        if (nextQuestionIndex !== -1) {
          const firstQuestionSpace = this.calculateQuestionSpace(
            items[nextQuestionIndex]
          );
          const requiredSpace = itemSpace + firstQuestionSpace;

          if (currentSpace + requiredSpace > maxSpacePerPage) {
            if (pageItems.length > 0) {
              break;
            }

            pageItems.push(item);
            currentSpace += itemSpace;
            lockedFirstQuestionIndex = nextQuestionIndex;
            continue;
          }
        }
      }

      if (pageItems.length === 0) {
        pageItems.push(item);
        currentSpace += itemSpace;
        continue;
      }

      const nextItemIndex = i + 1;
      const hasNextItem = nextItemIndex < items.length;

      if (hasNextItem && !isForcedPairItem) {
        const nextItem = items[nextItemIndex];
        const nextItemSpace = this.calculateQuestionSpace(nextItem);
        const totalSpaceWithNext = currentSpace + itemSpace + nextItemSpace;

        if (totalSpaceWithNext > maxSpacePerPage) {
          if (
            itemSpace < 30 &&
            nextItemSpace < 30 &&
            totalSpaceWithNext <= maxSpacePerPage + 10
          ) {
            pageItems.push(item);
            currentSpace += itemSpace;
            continue;
          } else if (
            itemSpace < 20 &&
            totalSpaceWithNext <= maxSpacePerPage + 15
          ) {
            pageItems.push(item);
            currentSpace += itemSpace;
            continue;
          } else {
            break;
          }
        }
      }

      if (currentSpace + itemSpace > maxSpacePerPage && !isForcedPairItem) {
        const questionData = item.type === 'question' ? item.data : item;

        if (questionData.type === 'single-image') {
          break;
        }

        if (itemSpace < 25 && currentSpace + itemSpace <= maxSpacePerPage + 8) {
          pageItems.push(item);
          currentSpace += itemSpace;
        } else {
          break;
        }
      } else {
        pageItems.push(item);
        currentSpace += itemSpace;
      }

      if (lockedFirstQuestionIndex !== null && i === lockedFirstQuestionIndex) {
        lockedFirstQuestionIndex = null;

        if (currentSpace > maxSpacePerPage) {
          break;
        }
      }

      const questionData = item.type === 'question' ? item.data : item;
      if (questionData.type === 'single-image' && pageItems.length >= 2) {
        break;
      }
    }

    while (
      pageItems.length > 0 &&
      pageItems[pageItems.length - 1]?.type === 'section-header'
    ) {
      pageItems.pop();
    }

    return pageItems;
  }

  private calculateQuestionSpace(item: any): number {
    if (item.type === 'section-header') {
      let space = 12;
      if (item.data?.instructions) {
        const instructionsText = this.stripHtmlTags(item.data.instructions);
        const instructionsLines = Math.ceil(instructionsText.length / 80);
        space += Math.max(4, instructionsLines * 2.5 + 8);
      }

      return space;
    }

    const question = item.type === 'question' ? item.data : item;

    let space = 25;

    const questionText = this.stripHtmlTags(
      question.question || question.title || ''
    );
    const questionTextLines = Math.ceil(questionText.length / 65);
    space += Math.max(1, questionTextLines) * 3;

    // Calculate space based on question type and options
    switch (question.type) {
      case 'single-image': {
        const imageCount = question.options?.length || 0;
        // Images take more space - estimate based on grid layout
        if (imageCount <= 2) {
          space += 45; // 2 images in a row
        } else if (imageCount <= 4) {
          space += 65; // 2x2 grid
        } else if (imageCount <= 6) {
          space += 85; // 2x3 grid
        } else {
          space += 105; // 3x3 or more
        }
        break;
      }
      case 'ranking': {
        const options = question.options || [];
        let optionSpace = 0;

        options.forEach((option: string) => {
          const optionText = this.stripHtmlTags(option);
          const optionLines = Math.ceil(optionText.length / 80);
          optionSpace += Math.max(4, optionLines * 3 + 4);
        });

        space += optionSpace + options.length * 1;
        space += 8;
        break;
      }
      case 'multiple-choose':
      case 'single-choose': {
        const options = question.options || [];
        let optionSpace = 0;

        options.forEach((option: string) => {
          const optionText = this.stripHtmlTags(option);
          const optionLines = Math.ceil(optionText.length / 80);
          optionSpace += Math.max(4, optionLines * 3 + 4);
        });

        space += optionSpace + options.length * 1;
        break;
      }
      case 'satisfactory-scale': {
        // const maxRanking = question.metadata?.maxRanking || 10;
        space += 20;
        break;
      }
      case 'free-text': {
        const lines = question.metadata?.textLinesLimit || 3;
        space += lines * 6;
        break;
      }
      case 'boolean':
      case 'yesno': {
        space += 8;
        break;
      }
      default: {
        space += 18;
        break;
      }
    }

    if (question.metadata?.useFollowUpQuestion) {
      const followUpText = this.stripHtmlTags(
        question.metadata.followUpQuestionText || ''
      );
      const followUpLines = Math.ceil(followUpText.length / 80);
      space += Math.max(4, followUpLines * 2.5 + 4);
    }

    space += 5;

    return Math.ceil(space);
  }

  private stripHtmlTags(html: string): string {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }

  /**
   * Debug method to log space calculations for questions
   * This can be useful for troubleshooting pagination issues
   */
  private logQuestionSpaceCalculations(questions: any[]): void {
    // console.log('=== Question Space Calculations ===');
    let totalSpace = 0;
    questions.forEach((question, index) => {
      const space = this.calculateQuestionSpace(question);
      const questionText = this.stripHtmlTags(
        question.question || question.title || ''
      );
      const optionCount = question.options?.length || 0;
      const hasFollowUp = question.metadata?.useFollowUpQuestion || false;
      const followUpText = hasFollowUp
        ? this.stripHtmlTags(question.metadata.followUpQuestionText || '')
        : '';

      totalSpace += space;
    });

    let currentPage = 1;
    let questionIndex = 0;
    while (questionIndex < questions.length) {
      const pageQuestions = this.getQuestionsForPage(questions, questionIndex);
      const pageSpace = pageQuestions.reduce(
        (sum, q) => sum + this.calculateQuestionSpace(q),
        0
      );
      const questionNumbers = pageQuestions
        .map((_, i) => questionIndex + i + 1)
        .join(', ');

      questionIndex += pageQuestions.length;
      currentPage++;
    }
  }

  private createPageContainer(): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: -99999px;
      left: -99999px;
      width: 210mm;
      height: 297mm;
      max-width: 210mm;
      background-color: white;
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.4;
      color: #000;
      padding: 12mm;
      box-sizing: border-box;
      overflow: visible;
      display: flex;
      flex-direction: column;
    `;
    document.body.appendChild(container);
    return container;
  }

  private async renderPageToPdf(
    pdf: jsPDF,
    container: HTMLElement
  ): Promise<void> {
    try {
      await this.waitForImagesToLoad(container);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvas = await html2canvas(container, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123,
        logging: false,
        imageTimeout: 0,
        removeContainer: true,
        onclone: (clonedDoc) => {
          const clonedImages = clonedDoc.querySelectorAll('img');
          clonedImages.forEach((img: any) => {
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.display = 'block';

            if (
              img.src &&
              (img.src.startsWith('http://') || img.src.startsWith('https://'))
            ) {
              img.crossOrigin = 'anonymous';
            }
          });

          const elements = clonedDoc.querySelectorAll(
            '.survey-question, .question-header'
          );
          elements.forEach((el: any) => {
            if (el.style) {
              el.style.pageBreakInside = 'avoid';
              el.style.breakInside = 'avoid';
            }
          });
        },
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
    } catch (error) {
      console.error('Error rendering page to PDF:', error);
      throw error;
    } finally {
      if (container && container.parentNode === document.body) {
        document.body.removeChild(container);
      }
    }
  }

  private async waitForImagesToLoad(container: HTMLElement): Promise<void> {
    const images = container.querySelectorAll('img');
    const imagePromises: Promise<void>[] = [];

    images.forEach((img, _index) => {
      if (img.complete && img.naturalWidth > 0) {
        return;
      }

      const promise = new Promise<void>((resolve) => {
        let settled = false;
        const timeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          resolve();
        }, 10000);

        const onLoad = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          resolve();
        };

        const onError = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          resolve();
        };

        img.addEventListener('load', onLoad);
        img.addEventListener('error', onError);
      });

      imagePromises.push(promise);
    });

    if (imagePromises.length > 0) {
      await Promise.all(imagePromises);
    }
  }

  private escapeHtml(text: string): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private arrayBufferToDataUrl(
    buffer: ArrayBuffer,
    mimeType: string = 'image/png'
  ): string {
    const uint8Array = new Uint8Array(buffer);
    const binaryString = Array.from(uint8Array)
      .map((byte) => String.fromCharCode(byte))
      .join('');
    const base64String = btoa(binaryString);
    return `data:${mimeType};base64,${base64String}`;
  }

  private processImageSrc(imageSrc: any): string {
    if (!imageSrc) return '';

    if (typeof imageSrc === 'string' && imageSrc.startsWith('data:')) {
      return imageSrc;
    }

    if (
      typeof imageSrc === 'string' &&
      (imageSrc.startsWith('http://') || imageSrc.startsWith('https://'))
    ) {
      return imageSrc;
    }

    if (imageSrc instanceof ArrayBuffer) {
      return this.arrayBufferToDataUrl(imageSrc);
    }

    if (typeof imageSrc === 'string' && !imageSrc.startsWith('http')) {
      return `data:image/png;base64,${imageSrc}`;
    }

    return imageSrc;
  }

  private processRichText(html: string): string {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach((script) => script.remove());

    return tempDiv.innerHTML || this.escapeHtml(html);
  }

  private generateSectionHeaderHtml(
    sectionTitle: string,
    instructions?: string
  ): string {
    return `
      <div class="section-header" style="margin: 15px 0 10px 0; padding: 8px 0; border-bottom: 2px solid #007cba;">
        <h4 style="margin: 0; font-size: 1.1rem; font-weight: 600; color: #007cba;">
          ${this.escapeHtml(sectionTitle)}
        </h4>
        ${
          instructions
            ? `
          <div style="margin: 10px 0 0 0; font-size: 0.85rem; color: #555; line-height: 1.5; padding: 8px; background: #f9fafb; border-left: 3px solid #007cba; border-radius: 3px;">
            ${this.processRichText(instructions)}
          </div>
        `
            : ''
        }
      </div>
    `;
  }

  private generateQuestionHtml(
    question: any,
    questionNumber: number,
    assets: any
  ): string {
    const isRequired = question.isRequired;
    const instructions = this.getInstructionsByType(
      question.type,
      question.metadata
    );

    return `
      <div class="survey-question" style="width: 100%; margin-bottom: 20px; font-size: 14px; page-break-inside: avoid; background: #fafafa; padding: 15px; border: 1px solid #ddd; border-radius: 6px; box-sizing: border-box; overflow: hidden;">
        <label class="question-label" style="display: block; margin-bottom: 1rem;">
          <div class="question-header" style="display: flex; align-items: flex-start; gap: 0.5rem; margin-bottom: 0.5rem;">
            <span class="question-number" style="
              color: #4a90e2;
              font-weight: 600;
              min-width: 1.5rem;
              height: 1.5rem;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              background: #f0f7ff;
              border-radius: 50%;
              font-size: 0.8rem;
              margin-right: 3px;
              flex-shrink: 0;
            ">${questionNumber}</span>

            <span class="question-text" style="
              color: #212529;
              font-size: 1rem;
              font-weight: 500;
              line-height: 1.4;
              flex: 1;
              word-wrap: break-word;
              max-width: calc(100% - 2rem);
            ">${this.processRichText(question.question || question.title || 'Question')}</span>

            ${isRequired ? `<span class="required" style="color: #dc3545; font-size: 0.75rem; font-weight: 500; padding: 0.2rem 0.4rem; background: #fff5f5; border-radius: 3px; flex-shrink: 0;">Required</span>` : ''}
          </div>

          ${instructions ? `<span class="instructions" style="display: block; color: #6c757d; font-size: 0.8rem; margin-top: 0.5rem; padding-left: 2rem; line-height: 1.3;">${instructions}</span>` : ''}
        </label>

        <div class="option-container" style="margin-top: 0.75rem; padding-left: 2rem; max-width: 100%; overflow: hidden;">
          ${this.generateQuestionOptionsHtml(question, questionNumber, assets)}
        </div>

        ${this.generateFollowUpHtml(question)}
      </div>
    `;
  }

  private generateQuestionOptionsHtml(
    question: any,
    questionNumber: number,
    assets: any
  ): string {
    const questionId = `question-${questionNumber}`;

    switch (question.type) {
      case 'boolean':
        return this.generateBooleanOptions(questionId);

      case 'yesno':
        return this.generateYesNoOptions(questionId);

      case 'single-choose':
        return this.generateSingleChooseOptions(
          question.options || [],
          questionId
        );

      case 'multiple-choose':
        return this.generateMultipleChooseOptions(
          question.options || [],
          questionId
        );

      case 'ranking':
        return this.generateRankingOptions(question.options || [], questionId);

      case 'satisfactory-scale':
        return this.generateSatisfactoryScaleOptions(
          question.metadata?.maxRanking || 10,
          questionId
        );

      case 'free-text':
        return this.generateFreeTextOptions(question.metadata, questionId);

      case 'single-image':
        return this.generateImageOptions(question, questionNumber, assets);

      default:
        return this.generateDefaultTextInput(questionId);
    }
  }

  private generateBooleanOptions(questionId: string): string {
    return `
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
            <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 50%; display: inline-block; margin-top: 0.1rem;"></span>
          </td>
          <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
            <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">True</label>
          </td>
        </tr>
        <tr>
          <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
            <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 50%; display: inline-block; margin-top: 0.1rem;"></span>
          </td>
          <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
            <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">False</label>
          </td>
        </tr>
      </table>
    `;
  }

  private generateYesNoOptions(_questionId: string): string {
    return `
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
            <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 50%; display: inline-block; margin-top: 0.1rem;"></span>
          </td>
          <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
            <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">Yes</label>
          </td>
        </tr>
        <tr>
          <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
            <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 50%; display: inline-block; margin-top: 0.1rem;"></span>
          </td>
          <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
            <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">No</label>
          </td>
        </tr>
      </table>
    `;
  }

  private generateSingleChooseOptions(
    options: string[],
    _questionId: string
  ): string {
    return `
      <table style="width: 100%; border-collapse: collapse;">
        ${options
          .map(
            (option) => `
          <tr>
            <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
              <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 50%; display: inline-block; margin-top: 0.1rem;"></span>
            </td>
            <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
              <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">${this.processRichText(option)}</label>
            </td>
          </tr>
        `
          )
          .join('')}
      </table>
    `;
  }

  private generateMultipleChooseOptions(
    options: string[],
    _questionId: string
  ): string {
    return `
      <table style="width: 100%; border-collapse: collapse;">
        ${options
          .map(
            (option) => `
          <tr>
            <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
              <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 3px; display: inline-block; margin-top: 0.1rem;"></span>
            </td>
            <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
              <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">${this.processRichText(option)}</label>
            </td>
          </tr>
        `
          )
          .join('')}
      </table>
    `;
  }

  private generateRankingOptions(
    options: string[],
    _questionId: string
  ): string {
    return `
      <div style="margin-bottom: 0.5rem;">
        <p style="color: #6c757d; font-size: 0.8rem; margin-bottom: 0.5rem;">Rank the options below (1 = best, ${options.length} = worst):</p>
      </div>
      <table style="width: 100%; border-collapse: collapse;">
        ${options
          .map(
            (option, index) => `
          <tr>
            <td style="width: 1.5rem; height: 1.5rem; vertical-align: top; padding: 0.25rem 0.5rem 0.25rem 0;">
              <span style="
                width: 1.2rem;
                height: 1.2rem;
                border: 2px solid #4a90e2;
                border-radius: 3px;
                display: inline-block;
                margin-top: 0.1rem;
                background: #f0f7ff;
                text-align: center;
                font-size: 0.7rem;
                line-height: 0.8rem;
                color: #4a90e2;
                font-weight: bold;
              "> </span>
            </td>
            <td style="height: 1.5rem; vertical-align: top; padding: 0.25rem 0;">
              <label style="color: #495057; font-size: 0.9rem; cursor: pointer; line-height: 1.3; display: block; margin-top: -10px;">${this.processRichText(option)}</label>
            </td>
          </tr>
        `
          )
          .join('')}
      </table>
    `;
  }

  private generateSatisfactoryScaleOptions(
    maxRanking: number,
    _questionId: string
  ): string {
    const range = Array.from({ length: maxRanking }, (_, i) => i + 1);

    return `
      <div style="margin: 1rem 0;">
        <table style="width: 100%; border-collapse: separate; border-spacing: 0;">
          <tr>
            ${range
              .map(
                (num) => `
              <td style="text-align: center; padding: 0; width: ${100 / maxRanking}%;">
                <span style="display: block; width: 1.5rem; height: 1.5rem; border: 2px solid #666; border-radius: 50%; margin: 0 auto; background: white;"></span>
              </td>
            `
              )
              .join('')}
          </tr>
          <tr>
            ${range
              .map(
                (num) => `
              <td style="text-align: center; padding: 0;">
                <span style="display: block; font-size: 1rem; font-weight: bold; color: #495057; margin-top: 0.5rem;">${num}</span>
              </td>
            `
              )
              .join('')}
          </tr>
        </table>
      </div>
    `;
  }

  private generateFreeTextOptions(metadata: any, _questionId: string): string {
    const textDataType = metadata?.textDataType;
    const maxLength = metadata?.textLimit || 4000;
    const textLinesLimit = metadata?.textLinesLimit || 3;

    switch (textDataType) {
      case 'numeric': {
        return `
          <div style="margin-top: 0.75rem;">
            <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.75rem; background: white; min-height: 2.5rem;">
              <span style="color: #999; font-style: italic;">Numeric input (max: ${maxLength})</span>
            </div>
          </div>
        `;
      }

      case 'email': {
        return `
          <div style="margin-top: 0.75rem;">
            <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.75rem; background: white; min-height: 2.5rem; display: flex; align-items: center;">
              <span style="margin-right: 0.5rem;">📧</span>
              <span style="color: #999; font-style: italic;">Email address</span>
            </div>
          </div>
        `;
      }

      case 'url': {
        return `
          <div style="margin-top: 0.75rem;">
            <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.75rem; background: white; min-height: 2.5rem; display: flex; align-items: center;">
              <span style="margin-right: 0.5rem;">🔗</span>
              <span style="color: #999; font-style: italic;">Website URL</span>
            </div>
          </div>
        `;
      }

      case 'phone-number': {
        return `
          <div style="margin-top: 0.75rem;">
            <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.75rem; background: white; min-height: 2.5rem; display: flex; align-items: center;">
              <span style="margin-right: 0.5rem;">📞</span>
              <span style="color: #999; font-style: italic;">Phone number (000) 000-0000</span>
            </div>
          </div>
        `;
      }

      default: {
        // multi-lines, alphanumeric, or undefined
        const height = Math.max(textLinesLimit * 1.5, 3) + 'rem';
        return `
          <div style="margin-top: 0.75rem;">
            <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.75rem; background: white; min-height: ${height}; width: 100%;">
              <span style="color: #999; font-style: italic;">Text answer (max: ${maxLength} characters)</span>
            </div>
          </div>
        `;
      }
    }
  }

  private generateImageOptions(
    question: any,
    questionNumber: number,
    assets: any
  ): string {
    if (!question.options || !question.metadata?.assets) {
      return this.generateDefaultTextInput(`question-${questionNumber}`);
    }

    const imagesPerRow = question.options.length > 4 ? 3 : 2;
    const imageWidth = imagesPerRow === 3 ? '130px' : '160px';
    const imageHeight = imagesPerRow === 3 ? '100px' : '120px';

    return `
      <div style="display: grid; grid-template-columns: repeat(${imagesPerRow}, 1fr); gap: 1rem; margin-top: 1rem; width: 100%; max-width: 100%;">
        ${question.options
          .map((option: string, index: number) => {
            const asset = question.metadata.assets[index];
            let imageSrc = null;

            if (asset?.imageBuffer) {
              imageSrc = this.processImageSrc(asset.imageBuffer);
            } else if (asset?.imageKey && assets && assets[asset.imageKey]) {
              imageSrc = this.processImageSrc(assets[asset.imageKey]);
            } else if (
              asset?.imageKey &&
              assets &&
              assets[`${asset.imageKey}-thumb`]
            ) {
              imageSrc = this.processImageSrc(
                assets[`${asset.imageKey}-thumb`]
              );
            }

            return `
            <div style="display: flex; flex-direction: column; align-items: center; margin-bottom: 1rem; max-width: 100%; overflow: hidden;">
              ${
                imageSrc
                  ? `
                <img src="${imageSrc}"
                     crossorigin="anonymous"
                     style="width: ${imageWidth}; height: ${imageHeight}; object-fit: contain; border-radius: 6px; border: 2px solid #ddd; margin-bottom: 0.5rem; background: #f8f9fa; display: block;"
                     alt="${this.escapeHtml(option)}">
              `
                  : `
                <div style="width: ${imageWidth}; height: ${imageHeight}; border: 2px dashed #ccc; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin-bottom: 0.5rem; background: #f8f9fa;">
                  <span style="color: #6c757d; font-size: 0.75rem; text-align: center;">Image not available</span>
                </div>
              `
              }
              <div style="display: flex; align-items: flex-start; width: 100%; justify-content: center; gap: 0.5rem;">
                <span style="width: 1rem; height: 1rem; border: 2px solid #666; border-radius: 50%; margin-top: 0.1rem; display: inline-block; flex-shrink: 0;"></span>
                <label style="color: #495057; font-size: 0.85rem; text-align: center; line-height: 1.2; word-wrap: break-word; max-width: calc(100% - 1.5rem); margin-top: -5px;">${this.processRichText(option)}</label>
              </div>
            </div>
          `;
          })
          .join('')}
      </div>
    `;
  }

  private generateDefaultTextInput(_questionId: string): string {
    return `
      <div style="margin-top: 0.75rem;">
        <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.75rem; background: white; min-height: 3rem;">
          <span style="color: #999; font-style: italic;">Answer:</span>
        </div>
      </div>
    `;
  }

  private generateFollowUpHtml(question: any): string {
    if (
      !question.metadata?.useFollowUpQuestion ||
      !question.metadata?.followUpQuestionText
    ) {
      return '';
    }

    return `
      <div style="margin-top: 5px; padding: 0.75rem; background: #f0f8ff; border-left: 3px solid #007cba; border-radius: 4px; max-width: 100%; box-sizing: border-box;">
        <label style="display: block; color: #007cba; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.85rem; line-height: 1.3; margin-top: -10px;">
          Follow-up: ${this.processRichText(question.metadata.followUpQuestionText)}
        </label>
        <div style="border: 1px solid #ccc; border-radius: 4px; padding: 0.5rem; background: white; min-height: 3rem;">
          <span style="color: #999; font-style: italic; font-size: 0.8rem;">Follow-up answer:</span>
        </div>
      </div>
    `;
  }

  private getInstructionsByType(type: string, metadata: any): string {
    switch (type) {
      case 'ranking':
        return 'Please click on the following options in order of ranking, from best, to worst';
      case 'satisfactory-scale':
        return `Rate on a scale from 1 to ${metadata?.maxRanking || 10}`;
      case 'multiple-choose':
        return 'Select all that apply';
      case 'single-choose':
        return 'Select one option';
      case 'boolean':
        return 'Select True or False';
      case 'yesno':
        return 'Select Yes or No';
      case 'free-text': {
        const textType = metadata?.textDataType;
        switch (textType) {
          case 'email':
            return 'Enter a valid email address';
          case 'url':
            return 'Enter a valid website URL';
          case 'phone-number':
            return 'Enter your phone number';
          case 'numeric':
            return 'Enter a number';
          default:
            return 'Enter your response in the text area below';
        }
      }
      case 'single-image':
        return 'Select one image';
      default:
        return '';
    }
  }

  openPdfInNewTab(filename: string, forcePrint: boolean = false): void {
    if (this.pdfBlob) {
      const url = window.URL.createObjectURL(this.pdfBlob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        if (forcePrint) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        // Revoke URL after window opens to prevent memory leak
        // Use setTimeout to ensure the window has loaded the URL
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        // If window.open failed, revoke immediately
        window.URL.revokeObjectURL(url);
      }
    }
  }

  downloadPdf(filename: string): void {
    if (this.pdfBlob) {
      const url = window.URL.createObjectURL(this.pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }

  getPdfBlob(): Blob | null {
    return this.pdfBlob;
  }

  cancelGeneration(): void {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }
  }
}
